import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFieldArray, useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import { FormError } from '@/components/ui/form-error';
import { PageLoading } from '@/components/ui/loading';
import { Camera, Plus, Trash2, Save, Send } from 'lucide-react';
import type { Project, LabourCategory, ApprovedRequirementOption } from '@/lib/types';
import { requiredText, dateFieldRule, rateRule, quantityRule, positiveIntegerRule, positiveNumberRule, timeRangeSuperRefine, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField, handleApiSubmitError } from '@/lib/form-validation';
import { cn } from '@/lib/utils';
import { workedMinutes, workerTotal, formatWorkedHours } from '@/lib/actual-labour';
import { validateTimeRange } from '@/lib/validation';
import { requirementPrimaryLabel, requirementSecondaryLabel, requirementSearchText } from '@/lib/requirement-labels';
import { PhotographSection } from '@/components/photographs/PhotographSection';

interface Block { id: string; project_id: string; name: string; is_active: boolean; created_at: string; updated_at: string; project_name?: string; }
interface Activity { id: string; project_id: string; block_id: string; name: string; default_unit_id?: string | null; is_active: boolean; default_unit_name?: string | null; }

const lumpsumSchema = z.object({
  categoryId: requiredText('Category', 100),
  mandays: positiveNumberRule('Mandays must be positive.'),
  rate: z.coerce.number().min(0, 'Rate must be zero or positive.').optional().default(0),
});

interface WorkerCategoryForm {
  workerCategoryId: string;
  count: number;
  rate: number;
  timeIn?: string;
  timeOut?: string;
}

interface ActivityForm {
  activityId: string;
  qty: number;
  unitId?: string;
  workerCategories: WorkerCategoryForm[];
}

interface LumpSumForm {
  categoryId: string;
  mandays: number;
  rate: number;
}

interface FormData {
  projectId: string;
  date: string;
  blockId: string;
  requirementId: string;
  activities: ActivityForm[];
  additionalLumpsumRequirements?: LumpSumForm[];
}

export default function ActualStatusFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const shouldSubmitRef = useRef(false);
  // Photograph files selected in the section below, uploaded as part of the
  // save flow so a new record (which has no id yet) can carry photographs too.
  const pendingPhotosRef = useRef<File[]>([]);
  // True when a create succeeded but a photograph upload afterwards failed, so
  // the error handler can send the user to the edit screen instead of letting
  // them create a duplicate record on retry.
  const createdForPhotoRef = useRef(false);
  const createdIdRef = useRef<string | null>(null);
  // Guards against a double-click on Save Draft / Send for Approval creating a
  // second record (and re-uploading the same photographs).
  const submittingRef = useRef(false);
  // Tracks whether edit-mode hydration has completed so effects don't clear
  // restored values while async master data (blocks, activities) loads.
  const hasHydratedEditRef = useRef(false);
  // Stores saved unit names from the detail response so the read-only Unit field
  // can display them even when the activity master is inactive and not in the
  // loaded activities list.
  const savedUnitNamesRef = useRef<Record<string, string>>({});
  const [editHydrated, setEditHydrated] = useState(false);

  const schema = useMemo(() => z.object({
    projectId: requiredText('Project', 100),
    date: dateFieldRule('Date'),
    blockId: requiredText('Block', 100),
    requirementId: z.string().optional(),
    activities: z.array(z.object({
      activityId: requiredText('Activity', 100),
      qty: quantityRule,
      unitId: z.string().optional(),
      workerCategories: z.array(z.object({
        workerCategoryId: requiredText('Worker category', 100),
        count: positiveIntegerRule('Count must be positive.'),
        rate: rateRule,
        timeIn: z.string().min(1, 'Time In is required.'),
        timeOut: z.string().min(1, 'Time Out is required.'),
      })).min(1, 'At least one worker category per activity.'),
    })).min(1, 'At least one activity is required.'),
    additionalLumpsumRequirements: z.array(lumpsumSchema).optional(),
  }).superRefine((data, ctx) => {
    for (let ai = 0; ai < data.activities.length; ai++) {
      const act = data.activities[ai];
      for (let wi = 0; wi < act.workerCategories.length; wi++) {
        const w = act.workerCategories[wi];
        if (!w.timeIn || !w.timeOut) continue;
        const result = validateTimeRange(w.timeIn, w.timeOut, {
          startLabel: 'Time In', endLabel: 'Time Out',
        });
        if (!result.valid) {
          if (result.startError) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['activities', ai, 'workerCategories', wi, 'timeIn'], message: result.startError });
          if (result.endError) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['activities', ai, 'workerCategories', wi, 'timeOut'], message: result.endError });
        }
      }
    }
  }), []);

  const { register, handleSubmit, control, watch, setValue, reset, clearErrors, setError, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      activities: [{ activityId: '', qty: 0, unitId: '', workerCategories: [{ workerCategoryId: '', count: 0, rate: 0, timeIn: '', timeOut: '' }] }],
      additionalLumpsumRequirements: [],
    },
  });

  const prevProjectBlockRef = useRef({ projectId: '', blockId: '' });

  const { fields: activityFields, append: appendActivity, remove: removeActivity } = useFieldArray({ control, name: 'activities' });

  const watchedProjectId = watch('projectId');
  const watchedBlockId = watch('blockId');
  const watchedActivities = watch('activities');
  const watchedRequirementId = watch('requirementId');

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => { const r = await api.get<any>('/projects?availability=active'); return (r.data ?? []) as Project[]; },
  });

  const { data: blocks, isLoading: blocksIsLoading } = useQuery({
    queryKey: ['blocks', watchedProjectId, isEdit],
    queryFn: async () => {
      const qs = new URLSearchParams({ projectId: watchedProjectId });
      if (isEdit) qs.set('includeInactive', '1');
      const r = await api.get<any>(`/blocks/all?${qs.toString()}`);
      return (r.data ?? []) as Block[];
    },
    enabled: Boolean(watchedProjectId),
  });

  const { data: activities, isLoading: activitiesIsLoading } = useQuery({
    queryKey: ['activities', watchedProjectId, watchedBlockId, isEdit],
    queryFn: async () => {
      const qs = new URLSearchParams({ projectId: watchedProjectId, blockId: watchedBlockId });
      if (isEdit) qs.set('includeInactive', '1');
      const r = await api.get<any>(`/activities/all?${qs.toString()}`);
      return (r.data ?? []) as Activity[];
    },
    enabled: Boolean(watchedProjectId) && Boolean(watchedBlockId),
  });

  const { data: labourCategories } = useQuery({
    queryKey: ['labour-categories-active'],
    queryFn: async () => { const r = await api.get<any>('/labour-categories/all'); return (r.data ?? []) as LabourCategory[]; },
  });

  // Approved Requirements for the selected project (actual:create-gated).
  // retry:0 keeps a deterministic API failure from hammering the Worker with
  // repeat requests.
  const {
    data: approvedRequirements,
    isLoading: requirementsLoading,
    isError: requirementsError,
    refetch: refetchRequirements,
  } = useQuery({
    queryKey: ['requirements-approved', watchedProjectId],
    queryFn: async () => {
      const r = await api.get<any>(`/requirements/approved?projectId=${watchedProjectId}`);
      return (r.data ?? []) as ApprovedRequirementOption[];
    },
    enabled: Boolean(watchedProjectId),
    retry: 0,
  });

  const requirementOptions: SearchableSelectOption[] = (approvedRequirements ?? []).map((r) => ({
    value: r.id,
    label: requirementPrimaryLabel(r),
    description: requirementSecondaryLabel(r),
    searchText: requirementSearchText(r),
    sublabel: r.requirement_number,
  }));

  useEffect(() => {
    if (!isEdit) {
      const prev = prevProjectBlockRef.current;
      const projectChanged = prev.projectId && prev.projectId !== watchedProjectId;
      const blockChanged = prev.blockId && prev.blockId !== watchedBlockId;
      prevProjectBlockRef.current = { projectId: watchedProjectId || '', blockId: watchedBlockId || '' };

      if (projectChanged || blockChanged) {
        if (projectChanged) {
          setValue('blockId', '');
          setValue('requirementId', '');
          clearErrors('requirementId');
        }
        watchedActivities?.forEach((_, actIdx) => {
          setValue(`activities.${actIdx}.activityId`, '');
          setValue(`activities.${actIdx}.unitId`, '');
          clearErrors(`activities.${actIdx}.activityId`);
          clearErrors(`activities.${actIdx}.unitId`);
        });
      }
    }
  }, [watchedProjectId, watchedBlockId, isEdit, setValue, clearErrors, watchedActivities]);

  useEffect(() => {
    if (isEdit && !hasHydratedEditRef.current) return;
    watchedActivities?.forEach((act, actIdx) => {
      if (act?.activityId) {
        const actObj = activities?.find((a: Activity) => a.id === act.activityId);
        if (actObj) {
          clearErrors(`activities.${actIdx}.unitId`);
          const resolvedUnitId = actObj.default_unit_id || '';
          if (act.unitId !== resolvedUnitId) {
            setValue(`activities.${actIdx}.unitId`, resolvedUnitId);
          }
        }
      }
    });
  }, [watchedActivities, activities, setValue, clearErrors, isEdit]);

  const { data: existingRecord, isLoading: loadingRecord } = useQuery({
    queryKey: ['actual-status', id],
    queryFn: async () => { const r = await api.get<any>(`/actual-status/${id}`); return r.data; },
    enabled: isEdit && Boolean(id),
  });

  useEffect(() => {
    if (existingRecord && isEdit && !hasHydratedEditRef.current) {
      const unitNames: Record<string, string> = {};
      const activitiesWithWorkers = (existingRecord.activities || []).map((a: any) => {
        if (a.unit_id && a.unit_name) unitNames[a.activity_id || a.activityId || ''] = a.unit_name;
        const actWorkers = (existingRecord.workers || [])
          .filter((w: any) => w.activity_id === a.id || !w.activity_id)
          .map((w: any) => ({
            workerCategoryId: w.worker_category_id,
            count: w.actual_count || 0,
            rate: w.rate || 0,
            timeIn: w.time_in || '',
            timeOut: w.time_out || '',
          }));
        return {
          activityId: a.activity_id || a.activityId,
          qty: a.completed_quantity || a.qty || 0,
          unitId: a.unit_id || '',
          workerCategories: actWorkers.length > 0 ? actWorkers : [{ workerCategoryId: '', count: 0, rate: 0, timeIn: '', timeOut: '' }],
        };
      });
      savedUnitNamesRef.current = unitNames;
      reset({
        projectId: existingRecord.project_id,
        date: existingRecord.work_date,
        blockId: existingRecord.block_id,
        requirementId: existingRecord.requirement_id || '',
        activities: activitiesWithWorkers,
        additionalLumpsumRequirements: (existingRecord.lumpsum || []).map((l: any) => ({
          categoryId: l.worker_category_id || l.categoryId,
          mandays: l.mandays || 0,
          rate: l.rate || 0,
        })),
      });
      hasHydratedEditRef.current = true;
      setEditHydrated(true);
    }
  }, [existingRecord, isEdit, reset]);

  useEffect(() => {
    if (isEdit) {
      hasHydratedEditRef.current = false;
      setEditHydrated(false);
      savedUnitNamesRef.current = {};
    }
  }, [id, isEdit]);

  const saveMutation = useMutation({
    mutationFn: async ({ data, shouldSubmit }: { data: FormData; shouldSubmit: boolean }) => {
      const payload: Record<string, any> = {
        projectId: data.projectId,
        date: data.date,
        blockId: data.blockId,
        activities: data.activities.map(a => {
          const selectedAct = activities?.find((act: Activity) => act.id === a.activityId);
          return {
            activityId: a.activityId,
            qty: a.qty,
            unitId: selectedAct?.default_unit_id || undefined,
            workerCategories: (a.workerCategories || []).map(w => ({
              workerCategoryId: w.workerCategoryId,
              count: w.count,
              rate: w.rate,
              timeIn: w.timeIn || undefined,
              timeOut: w.timeOut || undefined,
            })),
          }
        }),
        additionalLumpsumRequirements: (data.additionalLumpsumRequirements || []).map(l => ({
          categoryId: l.categoryId,
          mandays: l.mandays,
          rate: l.rate || 0,
        })),
      };
      // New Actual Labour records must link an approved Requirement (legacy
      // records may be edited without one).
      if (!isEdit) payload.requirementId = data.requirementId;

      let entityId = id;

      if (isEdit) {
        await api.put<any>(`/actual-status/${id}`, payload);
      } else {
        const res = await api.post<any>('/actual-status', payload);
        entityId = res.data?.id;
        createdIdRef.current = entityId ?? null;
      }

      const photoFiles = pendingPhotosRef.current || [];
      if (entityId && photoFiles.length > 0) {
        const formData = new FormData();
        for (const file of photoFiles) formData.append('photo', file);
        try {
          await api.upload(`/actual-status/${entityId}/photographs`, formData);
        } catch (err) {
          if (!isEdit) createdForPhotoRef.current = true;
          throw err;
        }
      }

      if (shouldSubmit && entityId) {
        await api.post(`/actual-status/${entityId}/submit`);
      }

      return { shouldSubmit };
    },
    onSuccess: (res) => {
      createdForPhotoRef.current = false;
      createdIdRef.current = null;
      addToast(
        res.shouldSubmit ? 'Actual status submitted for approval' : (isEdit ? 'Actual status updated' : 'Actual status saved as draft'),
        'success'
      );
      queryClient.invalidateQueries({ queryKey: ['actual-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['report-project-summary'] });
      queryClient.invalidateQueries({ queryKey: ['report-category-detail'] });
      queryClient.invalidateQueries({ queryKey: ['report-budget-actual'] });
      queryClient.invalidateQueries({ queryKey: ['report-multi-summary'] });
      navigate('/actuals');
    },
    onError: (err: any) => {
      // The record was created but a photograph upload failed part-way: send
      // the engineer to the edit screen so they can retry the upload instead
      // of accidentally creating a duplicate record by pressing Save again.
      if (createdForPhotoRef.current && createdIdRef.current && !isEdit) {
        createdForPhotoRef.current = false;
        const entityId = createdIdRef.current;
        createdIdRef.current = null;
        addToast('Actual Labour was created, but the photograph upload did not complete. Open the record to retry the upload.', 'error');
        navigate(`/actuals/${entityId}/edit`);
        return;
      }
      createdForPhotoRef.current = false;
      handleApiSubmitError(err, { addToast, setError: (name, error) => setError(name as any, error) });
    },
  });

  const onFormSubmit = (data: FormData) => {
    // New Actual Labour must be linked to an approved Requirement (legacy
    // records that predate the link may be edited without one). The selected id
    // must also be in the currently loaded approved list for the project, so a
    // stale/old requirement id can never be submitted.
    if (!isEdit) {
      const eligible = (approvedRequirements ?? []).some((r) => r.id === data.requirementId);
      if (!eligible) {
        setError('requirementId', { message: 'Select an approved Requirement.' });
        addToast('Select an approved Requirement.', 'error');
        return;
      }
    }
    // Ignore a second submit while the first is still in flight so a
    // double-click cannot create a duplicate record or re-upload photographs.
    if (submittingRef.current) return;
    submittingRef.current = true;
    saveMutation.mutate(
      { data, shouldSubmit: shouldSubmitRef.current },
      { onSettled: () => { submittingRef.current = false; } }
    );
  };

  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  useEffect(() => {
    const container = document.querySelector<HTMLElement>('.app-content-shell');
    if (container) {
      container.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, []);

  const activitiesLoaded = Array.isArray(activities);
  const isPending = saveMutation.isPending;

  const computeActivityTotal = (actIdx: number): number => {
    const act = watchedActivities?.[actIdx];
    if (!act?.workerCategories) return 0;
    return act.workerCategories.reduce((sum, w) => {
      const minutes = workedMinutes(w.timeIn, w.timeOut);
      if (minutes === null) return sum;
      return sum + (workerTotal(w.count || 0, minutes, w.rate || 0) ?? 0);
    }, 0);
  };

  const computeGrandTotal = (): number => {
    const workerTotal = watchedActivities
      ? watchedActivities.reduce((sum, _, idx) => sum + computeActivityTotal(idx), 0)
      : 0;
    const lumpsumTotal = (watch('additionalLumpsumRequirements') || []).reduce(
      (sum, l) => sum + (l.mandays || 0) * (l.rate || 0), 0
    );
    return workerTotal + lumpsumTotal;
  };

  const activitiesArrayError = Array.isArray(errors.activities) ? undefined : errors.activities?.message;

  if (isEdit && (loadingRecord || (!editHydrated && Boolean(existingRecord)) || (editHydrated && (blocksIsLoading || activitiesIsLoading)))) return <PageLoading />;

  return (
    <div className="actual-labour-page">
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-primary">{isEdit ? 'Edit Actual Labour' : 'New Actual Labour'}</h1>
        <p className="text-sm text-secondary mt-1">Enter activities with worker categories, time tracking and approvals.</p>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit, onInvalid)} className="actual-labour-form space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Project Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Project *</Label>
              <Select onValueChange={v => setValue('projectId', v)} value={watchedProjectId || ''}>
                <SelectTrigger data-field-name="projectId" aria-invalid={errors.projectId ? true : undefined} aria-describedby={errors.projectId ? 'projectId-error' : undefined}><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects?.map((p: Project) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <FormError id="projectId-error" message={errors.projectId?.message} />
            </div>
            <div>
              <Label>Requirement *</Label>
              {isEdit ? (
                <EditRequirementDisplay existingRecord={existingRecord} requirements={approvedRequirements} />
              ) : requirementsLoading ? (
                <div className="h-10 flex items-center px-3 rounded-md border bg-muted/40 text-sm text-secondary">Loading approved requirements...</div>
              ) : requirementsError ? (
                <div className="space-y-2">
                  <p className="text-sm text-error">Unable to load approved requirements.</p>
                  <Button type="button" variant="outline" size="sm" onClick={() => refetchRequirements()}>Retry</Button>
                </div>
              ) : (
                <>
                  <SearchableSelect
                    value={watchedRequirementId || ''}
                    onValueChange={v => setValue('requirementId', v)}
                    options={requirementOptions}
                    placeholder="Select approved requirement"
                    disabled={!watchedProjectId || requirementOptions.length === 0}
                    searchPlaceholder="Search by ID, location, description, dates or priority"
                    emptyMessage="No matching approved requirements."
                    data-field-name="requirementId"
                    aria-invalid={errors.requirementId ? true : undefined}
                    aria-describedby={errors.requirementId ? 'requirementId-error' : undefined}
                  />
                  {watchedProjectId && requirementOptions.length === 0 && (
                    <p className="text-xs text-secondary mt-1">No approved requirements available for this project.</p>
                  )}
                </>
              )}
              <FormError id="requirementId-error" message={errors.requirementId?.message} />
            </div>
            <div>
              <Label>Date *</Label>
              <Input type="date" {...register('date')} aria-invalid={errors.date ? true : undefined} aria-describedby={errors.date ? 'date-error' : undefined} className={cn(errors.date && 'border-error')} />
              <FormError id="date-error" message={errors.date?.message} />
            </div>
            <div>
              <Label>Block *</Label>
              <Select onValueChange={v => setValue('blockId', v)} value={watchedBlockId || ''} disabled={!watchedProjectId}>
                <SelectTrigger data-field-name="blockId" aria-invalid={errors.blockId ? true : undefined} aria-describedby={errors.blockId ? 'blockId-error' : undefined}><SelectValue placeholder="Select block" /></SelectTrigger>
                <SelectContent>
                  {blocks?.map((b: Block) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <FormError id="blockId-error" message={errors.blockId?.message} />
            </div>
          </CardContent>
        </Card>

        {activityFields.map((actField, actIdx) => {
          const act = watchedActivities?.[actIdx];
          const activityObj = activities?.find((a: Activity) => a.id === act?.activityId);
          const actErrors: any = Array.isArray(errors.activities) ? errors.activities?.[actIdx] : undefined;
          return (
            <Card key={actField.id}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Activity {actIdx + 1}</CardTitle>
                {activityFields.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeActivity(actIdx)}>
                    <Trash2 size={16} className="text-red-500 mr-1" /> Remove
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Activity *</Label>
                    <Select onValueChange={v => setValue(`activities.${actIdx}.activityId`, v)} value={act?.activityId || ''}>
                      <SelectTrigger data-field-name={`activities.${actIdx}.activityId`} aria-invalid={actErrors?.activityId ? true : undefined} aria-describedby={actErrors?.activityId ? `activities-${actIdx}-activityId-error` : undefined}><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {activities?.map((a: Activity) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormError id={`activities-${actIdx}-activityId-error`} message={actErrors?.activityId?.message} />
                  </div>
                  <div>
                    <Label>Qty *</Label>
                    <Input type="number" min={0} {...register(`activities.${actIdx}.qty`)} aria-invalid={actErrors?.qty ? true : undefined} aria-describedby={actErrors?.qty ? `activities-${actIdx}-qty-error` : undefined} className={cn(actErrors?.qty && 'border-error')} />
                    <FormError id={`activities-${actIdx}-qty-error`} message={actErrors?.qty?.message} />
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Input value={activityObj?.default_unit_name || savedUnitNamesRef.current[act?.activityId || ''] || ''} readOnly placeholder="Auto" />
                    {activitiesLoaded && Boolean(act?.activityId) && Boolean(activityObj) && !activityObj?.default_unit_id && (
                      <p className="text-xs text-red-500 mt-1">The selected activity does not have a default unit. Configure it in Activity Master.</p>
                    )}
                  </div>
                  <div className="flex items-end">
                    <div className="text-sm">
                      <span className="text-secondary">Activity Total: </span>
                      <span className="font-semibold">&#8377;{computeActivityTotal(actIdx).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>

                <WorkerCategoryRows
                  actIdx={actIdx}
                  control={control}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  labourCategories={labourCategories}
                  errors={errors}
                />
              </CardContent>
            </Card>
          );
        })}

        <div className="flex flex-col items-center gap-2">
          <Button type="button" variant="outline" onClick={() => appendActivity({ activityId: '', qty: 0, unitId: '', workerCategories: [{ workerCategoryId: '', count: 0, rate: 0, timeIn: '', timeOut: '' }] })}>
            <Plus size={14} /> Add Activity
          </Button>
          <FormError message={activitiesArrayError} />
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Additional Lump-sum Requirement (Mandays)</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => {
              const current = watch('additionalLumpsumRequirements') || [];
              setValue('additionalLumpsumRequirements', [...current, { categoryId: '', mandays: 0, rate: 0 }]);
            }}>
              <Plus size={14} /> Add Category
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {(watch('additionalLumpsumRequirements') || []).map((_, idx) => {
              const lsErrors: any = Array.isArray(errors.additionalLumpsumRequirements) ? errors.additionalLumpsumRequirements?.[idx] : undefined;
              const lsMandays = watch(`additionalLumpsumRequirements.${idx}.mandays`) || 0;
              const lsRate = watch(`additionalLumpsumRequirements.${idx}.rate`) || 0;
              const lsAmount = (lsMandays || 0) * (lsRate || 0);
              return (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end border rounded-lg p-3">
                  <div className="md:col-span-4">
                    <Label>Worker Category *</Label>
                    <Select onValueChange={v => setValue(`additionalLumpsumRequirements.${idx}.categoryId`, v)} value={watch(`additionalLumpsumRequirements.${idx}.categoryId`) || ''}>
                      <SelectTrigger data-field-name={`additionalLumpsumRequirements.${idx}.categoryId`} aria-invalid={lsErrors?.categoryId ? true : undefined} aria-describedby={lsErrors?.categoryId ? `lumpsum-${idx}-categoryId-error` : undefined}><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {labourCategories?.map((c: LabourCategory) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormError id={`lumpsum-${idx}-categoryId-error`} message={lsErrors?.categoryId?.message} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Mandays *</Label>
                    <Input type="number" min={0} step="0.5" {...register(`additionalLumpsumRequirements.${idx}.mandays`)} aria-invalid={lsErrors?.mandays ? true : undefined} aria-describedby={lsErrors?.mandays ? `lumpsum-${idx}-mandays-error` : undefined} className={cn(lsErrors?.mandays && 'border-error')} />
                    <FormError id={`lumpsum-${idx}-mandays-error`} message={lsErrors?.mandays?.message} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Rate (₹/manday)</Label>
                    <Input type="number" min={0} step="0.01" {...register(`additionalLumpsumRequirements.${idx}.rate`)} aria-invalid={lsErrors?.rate ? true : undefined} aria-describedby={lsErrors?.rate ? `lumpsum-${idx}-rate-error` : undefined} className={cn(lsErrors?.rate && 'border-error')} />
                    <FormError id={`lumpsum-${idx}-rate-error`} message={lsErrors?.rate?.message} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Amount</Label>
                    <div className="h-10 flex items-center text-sm font-medium">&#8377;{lsAmount.toLocaleString('en-IN')}</div>
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <Button type="button" variant="ghost" size="icon" onClick={() => {
                      const current = watch('additionalLumpsumRequirements') || [];
                      setValue('additionalLumpsumRequirements', current.filter((_, i) => i !== idx));
                    }}>
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Camera size={18} /> Photographs</CardTitle>
          </CardHeader>
          <CardContent>
            <PhotographSection
              entityId={isEdit ? id : undefined}
              savedPhotos={isEdit ? (existingRecord?.attachments || []) : []}
              canModify
              onPendingChange={(files) => { pendingPhotosRef.current = files; }}
            />
          </CardContent>
        </Card>

        <Card className="grand-total-card">
          <CardContent className="py-4">
            <div className="flex justify-end text-lg font-semibold">
              Grand Total: &#8377;{computeGrandTotal().toLocaleString('en-IN')}
            </div>
          </CardContent>
        </Card>

        <div className="actual-form-actions">
          <button type="button" className="btn-action btn-action--secondary" onClick={() => navigate('/actuals')}>Cancel</button>
          <button type="submit" className="btn-action btn-action--outline" onClick={() => { shouldSubmitRef.current = false; }} disabled={isPending}>
            {isPending ? null : <Save size={16} />} Save Draft
          </button>
          <button type="submit" className="btn-action btn-action--primary" onClick={() => { shouldSubmitRef.current = true; }} disabled={isPending}>
            {isPending ? null : <Send size={16} />} Send for Approval
          </button>
        </div>
      </form>
    </div>
  );
}

function EditRequirementDisplay({
  existingRecord,
  requirements,
}: {
  existingRecord: any;
  requirements?: ApprovedRequirementOption[];
}) {
  const requirementId = existingRecord?.requirement_id;
  const linked = requirements?.find((r: ApprovedRequirementOption) => r.id === requirementId);
  const fallback = existingRecord?.requirement_number || 'Not linked to a requirement';
  return (
    <div
      className="flex h-10 items-center justify-between gap-2 rounded-lg border bg-muted/40 px-3 text-sm text-primary"
      title={linked ? requirementSecondaryLabel(linked) : undefined}
    >
      <span className="truncate">{linked ? requirementPrimaryLabel(linked) : fallback}</span>
      {linked && <span className="shrink-0 text-xs text-secondary">{linked.requirement_number}</span>}
    </div>
  );
}

function WorkerCategoryRows({
  actIdx, control, register, watch, setValue, labourCategories, errors,
}: {
  actIdx: number;
  control: any;
  register: any;
  watch: any;
  setValue: any;
  labourCategories?: LabourCategory[];
  errors: any;
}) {
  const { fields, append, remove } = useFieldArray({ control, name: `activities.${actIdx}.workerCategories` });
  const watchedWorkers = watch(`activities.${actIdx}.workerCategories`);

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-secondary">Worker Categories</p>
        <Button type="button" variant="outline" size="sm" onClick={() => append({ workerCategoryId: '', count: 0, rate: 0, timeIn: '', timeOut: '' })}>
          <Plus size={14} /> Add Worker
        </Button>
      </div>
      <div className="worker-table-scroll">
      {fields.map((field, wIdx) => {
        const w = watchedWorkers?.[wIdx];
        const wMinutes = workedMinutes(w?.timeIn, w?.timeOut);
        const wTotal = wMinutes === null ? null : workerTotal(w?.count || 0, wMinutes, w?.rate || 0);
        const wcErrors: any = errors?.activities?.[actIdx]?.workerCategories?.[wIdx];
        return (
          <div key={field.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end mb-3 border rounded-lg p-3" style={{ minWidth: '760px' }}>
            <div className="md:col-span-3">
              <Label>Category *</Label>
              <Select onValueChange={v => setValue(`activities.${actIdx}.workerCategories.${wIdx}.workerCategoryId`, v)} value={w?.workerCategoryId || ''}>
                <SelectTrigger data-field-name={`activities.${actIdx}.workerCategories.${wIdx}.workerCategoryId`} aria-invalid={wcErrors?.workerCategoryId ? true : undefined} aria-describedby={wcErrors?.workerCategoryId ? `wc-${actIdx}-${wIdx}-categoryId-error` : undefined}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {labourCategories?.map((c: LabourCategory) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <FormError id={`wc-${actIdx}-${wIdx}-categoryId-error`} message={wcErrors?.workerCategoryId?.message} />
            </div>
            <div className="md:col-span-1">
              <Label>Count *</Label>
              <Input type="number" min={1} {...register(`activities.${actIdx}.workerCategories.${wIdx}.count`)} aria-invalid={wcErrors?.count ? true : undefined} aria-describedby={wcErrors?.count ? `wc-${actIdx}-${wIdx}-count-error` : undefined} className={cn(wcErrors?.count && 'border-error')} />
              <FormError id={`wc-${actIdx}-${wIdx}-count-error`} message={wcErrors?.count?.message} />
            </div>
            <div className="md:col-span-1">
              <Label>Rate (₹/hr) *</Label>
              <Input type="number" min={0} step="0.01" {...register(`activities.${actIdx}.workerCategories.${wIdx}.rate`)} aria-invalid={wcErrors?.rate ? true : undefined} aria-describedby={wcErrors?.rate ? `wc-${actIdx}-${wIdx}-rate-error` : undefined} className={cn(wcErrors?.rate && 'border-error')} />
              <FormError id={`wc-${actIdx}-${wIdx}-rate-error`} message={wcErrors?.rate?.message} />
            </div>
            <div className="md:col-span-1">
              <Label>Worked Hours</Label>
              <div className="h-10 flex items-center text-sm font-medium">{wMinutes === null ? '—' : formatWorkedHours(wMinutes)}</div>
            </div>
            <div className="md:col-span-1">
              <Label>Total</Label>
              <div className="h-10 flex items-center text-sm font-medium">{wTotal === null ? '—' : `₹${wTotal.toLocaleString('en-IN')}`}</div>
            </div>
            <div className="md:col-span-2">
              <Label>Time In *</Label>
              <Input type="time" {...register(`activities.${actIdx}.workerCategories.${wIdx}.timeIn`)} aria-invalid={wcErrors?.timeIn ? true : undefined} aria-describedby={wcErrors?.timeIn ? `wc-${actIdx}-${wIdx}-timeIn-error` : undefined} className={cn(wcErrors?.timeIn && 'border-error')} />
              <FormError id={`wc-${actIdx}-${wIdx}-timeIn-error`} message={wcErrors?.timeIn?.message} />
            </div>
            <div className="md:col-span-2">
              <Label>Time Out *</Label>
              <Input type="time" {...register(`activities.${actIdx}.workerCategories.${wIdx}.timeOut`)} aria-invalid={wcErrors?.timeOut ? true : undefined} aria-describedby={wcErrors?.timeOut ? `wc-${actIdx}-${wIdx}-timeOut-error` : undefined} className={cn(wcErrors?.timeOut && 'border-error')} />
              <FormError id={`wc-${actIdx}-${wIdx}-timeOut-error`} message={wcErrors?.timeOut?.message} />
            </div>
            <div className="md:col-span-1 flex justify-end">
              {fields.length > 1 && (
                <Button type="button" variant="ghost" size="icon" onClick={() => remove(wIdx)}>
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
